import { type ComponentProps, useCallback, useMemo, useRef } from "react";
import { appstr } from "@coreapp/utils/appstr";
import {
  EditableText,
  Flex,
  ListItemOption,
  Menu,
  OverflowText,
  Pill,
  Text
} from "@theorgchartdev/storybook";
import { enumBoxStyleSet, enumHierarchyTypes } from "@coreapp/constants";
import { QBoxLayout } from "@coreapp/CoreApp/QBoxLayout";
import { TemplateInfo } from "@coreapp/CoreApp/QChartTemplatesMgr";
import FieldTypePill from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/FieldsFormulasConfiguration/FieldTypePill";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { DeleteBoxStylePopover } from "./DeleteBoxStylePopover";
import { useBoxStylePreview } from "./useBoxStylePreview";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import * as StateLib from "@/Utils/StateLib";

const hierarchyTypePillProps: Record<number, "employee" | "position" | "group"> = {
  [enumHierarchyTypes.employees]: "employee",
  [enumHierarchyTypes.positions]: "position",
  [enumHierarchyTypes.grouping]: "group"
};

interface BoxStyleSummaryProps {
  layout: QBoxLayout;
  previewLayout?: QBoxLayout;
  previewVersion?: number;
  boxStyleSet: (typeof enumBoxStyleSet)[keyof typeof enumBoxStyleSet];
  viewCount: number;
  readOnly?: boolean;
  hasPendingEdit?: boolean;
  isActive?: boolean;
  getViewTemplates?: (boxStyleID: string) => TemplateInfo[];
  onViewNavigate?: (templateID: string) => void;
  onCopy: (layout: QBoxLayout) => void;
  onEdit: (layout: QBoxLayout) => void;
  onDelete: (layout: QBoxLayout) => Promise<void>;
  onRevert: (layout: QBoxLayout) => void;
  onRename: (layout: QBoxLayout, newName: string) => void;
}

const PREVIEW_MAX_HEIGHT = 80;

const BoxStyleSummary = ({
  layout,
  previewLayout,
  previewVersion,
  boxStyleSet,
  viewCount,
  readOnly,
  hasPendingEdit = false,
  isActive = false,
  getViewTemplates,
  onViewNavigate,
  onCopy,
  onEdit,
  onDelete,
  onRevert,
  onRename
}: BoxStyleSummaryProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useBoxStylePreview(
    canvasRef,
    previewLayout ?? layout,
    { maxPreviewHeight: PREVIEW_MAX_HEIGHT },
    previewVersion
  );
  const { handleSideBarModeChange, setSidebarBackTarget } = useRightSidebarContext();

  const validatorFn = useCallback(
    (text: string) => {
      if (!text.trim().length) {
        return {
          result: false,
          msg: appstr("Box style name cannot be empty"),
          showText: true,
          status: "error" as const
        };
      }
      const bsMgr = StateLib.GetWSMgr()?.GetBoxStylesMgr();
      if (!bsMgr?.IsUniqueBoxStyleName(text, layout)) {
        return {
          result: false,
          msg: appstr("Box style name must be unique"),
          showText: true,
          status: "error" as const
        };
      }
      return {
        result: true,
        msg: "",
        showText: false,
        status: undefined
      };
    },
    [layout]
  );

  const onClickDelete = useCallback(async () => {
    await onDelete(layout);
  }, [layout, onDelete]);

  const onViewSelect = useCallback(
    (templateID: string) => {
      const wsMgr = StateLib.GetWSMgr();
      // In BLE this is an editor exit, not an in-place swap, so route through
      // exitBoxLayout for the save/discard prompt; skip DoSetView to avoid changing the view on cancel.
      if (wsMgr?.fLayoutPanel?.EditBoxStyle?.()) {
        setSidebarBackTarget({
          mode: "boxlibrary",
          title: appstr("Back to box styles"),
          activeInMode: "view"
        });
        (wsMgr as any).exitBoxLayout?.({ view: String(templateID), tool: "view" });
        return;
      }
      if (wsMgr) {
        wsMgr.DoSetView(templateID);
        StateLib.ReRenderCanvas();
      }
      if (onViewNavigate) onViewNavigate(templateID);
      else handleSideBarModeChange("view");
    },
    [handleSideBarModeChange, onViewNavigate, setSidebarBackTarget]
  );

  const isSystem = boxStyleSet === enumBoxStyleSet.systemboxstyles;

  const boxStyleID = layout.GetBoxStyleID();

  const viewMenuOptions = useMemo(() => {
    if (!viewCount || !getViewTemplates) return [];
    return buildViewMenuOptions(getViewTemplates(boxStyleID), onViewSelect);
  }, [boxStyleID, viewCount, getViewTemplates, onViewSelect]);

  const viewsPill = (
    <Pill label={`${viewCount} views`} size="compact" onClick={viewCount ? () => {} : undefined} />
  );

  const unavailableViewCount = viewCount - viewMenuOptions.length;

  const actionTestIdPrefix = `boxstyles-summary-${isSystem ? "system" : "shared"}-${boxStyleID}`;

  const actions = useMemo(() => {
    const items: ComponentProps<typeof ActionListItem>["actions"] = [];
    if (!isSystem) {
      // Revert is per-card: only an edited, non-active card has a draft to undo (a tombstoned
      // delete has no card; the active box is reverted via the editor's own discard).
      if (hasPendingEdit && !isActive) {
        items.push({
          iconName: "clock-rotate-left" as const,
          onClick: () => onRevert(layout),
          tooltipProps: { message: appstr("Revert box style") },
          dataTestId: `${actionTestIdPrefix}-revert`
        });
      }
      items.push({
        iconName: "copy" as const,
        onClick: () => onCopy(layout),
        tooltipProps: { message: appstr("Copy box style") },
        dataTestId: `${actionTestIdPrefix}-copy`
      });
      // Edit and delete are no-ops/forbidden on the box currently open in the editor, so hide both.
      if (!isActive) {
        items.push(
          {
            iconName: "pen-to-square" as const,
            onClick: () => onEdit(layout),
            tooltipProps: { message: appstr("Edit box style") },
            dataTestId: `${actionTestIdPrefix}-edit`
          },
          {
            content: (
              <DeleteBoxStylePopover
                onConfirmDelete={onClickDelete}
                triggerTestId={`${actionTestIdPrefix}-delete`}
                dataTestPrefix={`${actionTestIdPrefix}-delete`}
              />
            )
          }
        );
      }
    } else {
      items.push({
        iconName: "copy" as const,
        onClick: () => onCopy(layout),
        tooltipProps: { message: appstr("Copy box style") },
        dataTestId: `${actionTestIdPrefix}-copy`
      });
    }
    return items;
  }, [
    actionTestIdPrefix,
    hasPendingEdit,
    isActive,
    isSystem,
    layout,
    onClickDelete,
    onCopy,
    onEdit,
    onRevert,
    viewCount
  ]);

  const nameWithIndicator = (node: JSX.Element) => (
    <Flex alignItems="center" gap="8px" overflow="hidden" className="oc-fill-width">
      {hasPendingEdit && (
        <Flex flex="0 0 auto">
          <Pill label={appstr("edited")} color="info" size="compact" />
        </Flex>
      )}
      {node}
    </Flex>
  );

  const summaryClasses = [
    "ocn-box-style-summary",
    hasPendingEdit && "ocn-box-style-summary--edited",
    isActive && "ocn-box-style-summary--active"
  ]
    .filter(Boolean)
    .join(" ");

  const displayName = (previewLayout ?? layout).GetBoxStyleName();

  return (
    <Flex className={summaryClasses} direction="column">
      {readOnly ? (
        nameWithIndicator(
          <OverflowText className="oc-flex-1 oc-overflow-hidden" modifier="medium" size="small">
            {displayName}
          </OverflowText>
        )
      ) : (
        <ActionListItem
          className="ocn-box-style-summary-header"
          actions={actions}
          visible="hover"
          padding="8px"
          buttonsInHierarchy
          contentBefore={
            isSystem
              ? nameWithIndicator(
                  <OverflowText
                    className="oc-flex-1 oc-overflow-hidden"
                    modifier="medium"
                    size="small"
                  >
                    {displayName}
                  </OverflowText>
                )
              : nameWithIndicator(
                  <EditableText
                    className="oc-flex-1 oc-overflow-hidden"
                    customClass="ocn-box-style-summary-title"
                    bold
                    text={displayName}
                    onChange={(text: string) => onRename?.(layout, text)}
                    validatorFn={validatorFn}
                  />
                )
          }
        />
      )}
      <Flex direction="column" gap="16px" padding="8px">
        <Flex alignItems="center" style={{ minHeight: PREVIEW_MAX_HEIGHT }}>
          <canvas ref={canvasRef} />
        </Flex>
        <Flex justifyContent="space-between">
          <Flex gap="8px" alignItems="center">
            <Text size="xsmall">{appstr("Applied to:")}</Text>
            {viewCount > 0 ? (
              <Menu
                dataTestId={chartModeTestId(
                  "sidebar",
                  `box-styles-applied-views-btn-${boxStyleID}`
                )}
                trigger={viewsPill}
                options={viewMenuOptions}
                listHeader={{ label: appstr("Go to a view:") }}
                listWidth={"medium"}
                position={"bottom-start"}
                listProps={{
                  footerNode:
                    unavailableViewCount > 0 ? (
                      <Flex className="ocn-box-style-view-list-footer" width="100%">
                        <Text size="xsmall" color="grey">
                          {appstr("views unavailable to access", {
                            viewCount: unavailableViewCount
                          })}
                        </Text>
                      </Flex>
                    ) : undefined
                }}
              />
            ) : (
              viewsPill
            )}
          </Flex>
          <Flex gap="8px">
            <Pill label={isSystem ? "System" : "Shared"} size="compact" />
            <FieldTypePill
              type={
                hierarchyTypePillProps[layout.GetHierarchyType() ?? enumHierarchyTypes.employees]
              }
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

const buildViewMenuOptions = (
  templates: TemplateInfo[],
  onViewSelect: (templateID: string) => void
): ListItemOption[] => {
  return templates.map(t => ({
    value: t.id,
    content: <Text size="small">{t.name}</Text>,
    clickHandler: () => onViewSelect(t.id)
  }));
};

export default BoxStyleSummary;
